import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../contexts/LanguageContext';
import { PIECE_SKIN_IMAGES } from '../../config/PieceSkins';

// Mini board preview component with size adjustment
const MiniBoardPreview = ({ whiteSkinId, blackSkinId, pieceSkins, pieceSize, onSizeChange }) => {
  const [previousSize, setPreviousSize] = useState(pieceSize);
  const [sizeDirection, setSizeDirection] = useState(null); // 'up', 'down', or null
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef(null);
  
  // Track size changes for comparison indicator
  useEffect(() => {
    if (pieceSize !== previousSize) {
      setSizeDirection(pieceSize > previousSize ? 'up' : 'down');
      setIsAnimating(true);
      
      // Clear previous timeout
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      
      // Reset animation after delay
      timeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
        setSizeDirection(null);
        setPreviousSize(pieceSize);
      }, 600);
    }
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [pieceSize, previousSize]);

  // Initial piece positions (simplified 4x4 mini board)
  const miniBoard = [
    ['r', 'n', 'b', 'q'],  // Black back row
    ['p', 'p', 'p', 'p'],  // Black pawns
    ['P', 'P', 'P', 'P'],  // White pawns
    ['R', 'N', 'B', 'Q'],  // White back row
  ];
  
  const pieceToType = {
    'k': 'king', 'q': 'queen', 'r': 'rook', 'b': 'bishop', 'n': 'knight', 'p': 'pawn',
    'K': 'king', 'Q': 'queen', 'R': 'rook', 'B': 'bishop', 'N': 'knight', 'P': 'pawn'
  };
  
  const unicodePieces = {
    white: { king: '♔', queen: '♕', rook: '♖', bishop: '♗', knight: '♘', pawn: '♙' },
    black: { king: '♚', queen: '♛', rook: '♜', bishop: '♝', knight: '♞', pawn: '♟' }
  };
  
  const renderPiece = (piece) => {
    if (!piece) return null;
    
    const isWhite = piece === piece.toUpperCase();
    const side = isWhite ? 'white' : 'black';
    const pieceType = pieceToType[piece.toLowerCase()];
    const skinId = isWhite ? whiteSkinId : blackSkinId;
    
    // Check if we have a skin image from PIECE_SKIN_IMAGES
    if (skinId && PIECE_SKIN_IMAGES[skinId] && PIECE_SKIN_IMAGES[skinId][pieceType]) {
      return (
        <img 
          src={PIECE_SKIN_IMAGES[skinId][pieceType]} 
          alt={`${side} ${pieceType}`}
          className={`mini-board-piece-img ${isAnimating ? 'size-animating' : ''}`}
          style={{ 
            width: `${pieceSize}%`, 
            height: `${pieceSize}%`,
            transition: 'width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.3s ease'
          }}
        />
      );
    }
    
    // Fallback to unicode
    return (
      <span 
        className={`mini-board-piece-unicode ${side} ${isAnimating ? 'size-animating' : ''}`}
        style={{ 
          fontSize: `${Math.round(32 * pieceSize / 85)}px`,
          transition: 'font-size 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.3s ease'
        }}
      >
        {unicodePieces[side][pieceType]}
      </span>
    );
  };
  
  // Calculate size difference for indicator
  const sizeDiff = pieceSize - 85; // Difference from default
  
  return (
    <div className="mini-board-preview" data-testid="mini-board-preview">
      {/* Size Adjustment Slider with Enhanced UI */}
      <div className="piece-size-control">
        <div className="size-header">
          <label htmlFor="piece-size-slider">
            Piece Size: <span className={`size-value ${sizeDirection ? `size-${sizeDirection}` : ''}`}>{pieceSize}%</span>
          </label>
          {/* Size Comparison Indicator */}
          <div className={`size-comparison ${sizeDiff !== 0 ? 'visible' : ''}`}>
            {sizeDiff > 0 ? (
              <span className="size-indicator larger">+{sizeDiff}% larger</span>
            ) : sizeDiff < 0 ? (
              <span className="size-indicator smaller">{sizeDiff}% smaller</span>
            ) : (
              <span className="size-indicator default">Default</span>
            )}
          </div>
        </div>
        <div className="slider-with-markers">
          <input
            id="piece-size-slider"
            type="range"
            min="50"
            max="100"
            value={pieceSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value);
              onSizeChange(newSize);
              // Apply immediately for real-time effect on actual board (unitless for calc())
              document.documentElement.style.setProperty('--piece-size', newSize);
            }}
            className="piece-size-slider"
            data-testid="piece-size-slider"
          />
          {/* Tick markers */}
          <div className="slider-ticks">
            <span className="tick" style={{left: '0%'}} title="50%"></span>
            <span className="tick default-tick" style={{left: '70%'}} title="85% (Default)"></span>
            <span className="tick" style={{left: '100%'}} title="100%"></span>
          </div>
        </div>
        <div className="size-labels">
          <span>Small (50%)</span>
          <span className="default-label">Default</span>
          <span>Large (100%)</span>
        </div>
      </div>
      
      <div className={`mini-board-grid ${isAnimating ? 'board-pulse' : ''}`}>
        {miniBoard.map((row, rowIdx) => (
          row.map((piece, colIdx) => {
            const isLight = (rowIdx + colIdx) % 2 === 0;
            return (
              <div 
                key={`${rowIdx}-${colIdx}`} 
                className={`mini-board-square ${isLight ? 'light' : 'dark'}`}
              >
                {renderPiece(piece)}
              </div>
            );
          })
        ))}
      </div>
      <div className="mini-board-labels">
        <span className="skin-label white-label">
          {pieceSkins?.find(s => s.id === whiteSkinId)?.name || 'Classic'}
        </span>
        <span className="vs-label">vs</span>
        <span className="skin-label black-label">
          {pieceSkins?.find(s => s.id === blackSkinId)?.name || 'Classic'}
        </span>
      </div>
    </div>
  );
};

const PieceSkinSelector = ({ 
  pieceSkins, // Array of available skins
  pieceSkinWhite, 
  setPieceSkinWhite, 
  pieceSkinBlack, 
  setPieceSkinBlack,
  onRandomize,
  pieceSize: externalPieceSize,
  setPieceSize: setExternalPieceSize
}) => {
  const { t } = useTranslation();
  const [showBoardPreview, setShowBoardPreview] = useState(true);
  // Use external pieceSize if provided, otherwise use internal state
  const [internalPieceSize, setInternalPieceSize] = useState(85);
  const pieceSize = externalPieceSize !== undefined ? externalPieceSize : internalPieceSize;
  const setPieceSize = setExternalPieceSize || setInternalPieceSize;

  // Render preview piece for the "Preview Both Sides" section
  const renderPreviewPiece = (pieceType, side, skinId) => {
    const unicodePieces = {
      white: { king: '♔', queen: '♕', rook: '♖', bishop: '♗', knight: '♘', pawn: '♙' },
      black: { king: '♚', queen: '♛', rook: '♜', bishop: '♝', knight: '♞', pawn: '♟' }
    };

    // Check if we have a skin image from PIECE_SKIN_IMAGES
    if (skinId && PIECE_SKIN_IMAGES[skinId] && PIECE_SKIN_IMAGES[skinId][pieceType]) {
      return (
        <div className={`preview-piece skinned ${side} skin-${skinId}`} key={`${pieceType}-${side}`}>
          <img src={PIECE_SKIN_IMAGES[skinId][pieceType]} alt={`${side} ${pieceType}`} />
        </div>
      );
    }

    // Fallback to unicode
    return (
      <div className={`preview-piece classic ${side}`} key={`${pieceType}-${side}`}>
        {unicodePieces[side][pieceType]}
      </div>
    );
  };

  return (
    <section className="settings-section">
      <h3>♟️ {t('settings.chessPieceSkins')}</h3>
      <p className="section-description">{t('settings.chessPieceSkinsDescription', 'Replace standard pieces with premium 3D rendered skins')}</p>
      
      {/* Mini Board Preview Toggle */}
      <div className="board-preview-section">
        <div className="preview-toggle-header">
          <h4>🎯 {t('settings.boardPreview', 'Board Preview')}</h4>
          <button 
            className="preview-toggle-btn"
            onClick={() => setShowBoardPreview(!showBoardPreview)}
            data-testid="toggle-board-preview"
          >
            {showBoardPreview ? '▼ Hide' : '▶ Show'}
          </button>
        </div>
        
        {showBoardPreview && (
          <div className="board-preview-wrapper">
            <MiniBoardPreview 
              whiteSkinId={pieceSkinWhite} 
              blackSkinId={pieceSkinBlack}
              pieceSkins={pieceSkins}
              pieceSize={pieceSize}
              onSizeChange={setPieceSize}
            />
            <p className="preview-hint">{t('settings.previewHint', 'See how your selected pieces will look on the board')}</p>
          </div>
        )}
      </div>
      
      {/* Preview Both Sides */}
      <div className="skin-preview-container">
        <h4>👁️ {t('settings.previewBothSides', 'Preview Both Sides')}</h4>
        <div className="skin-preview-board">
          {/* White pieces preview */}
          <div className="preview-side white-side">
            <span className="preview-label">⬜ {t('settings.whiteSidePieces', 'White')}</span>
            <div className="preview-pieces">
              {renderPreviewPiece('king', 'white', pieceSkinWhite)}
              {renderPreviewPiece('queen', 'white', pieceSkinWhite)}
              {renderPreviewPiece('rook', 'white', pieceSkinWhite)}
              {renderPreviewPiece('bishop', 'white', pieceSkinWhite)}
              {renderPreviewPiece('knight', 'white', pieceSkinWhite)}
              {renderPreviewPiece('pawn', 'white', pieceSkinWhite)}
            </div>
            <span className="preview-skin-name">{pieceSkins.find(s => s.id === pieceSkinWhite)?.name || 'Classic'}</span>
          </div>
          
          <div className="preview-vs">VS</div>
          
          {/* Black pieces preview */}
          <div className="preview-side black-side">
            <span className="preview-label">⬛ {t('settings.blackSidePieces', 'Black')}</span>
            <div className="preview-pieces">
              {renderPreviewPiece('king', 'black', pieceSkinBlack)}
              {renderPreviewPiece('queen', 'black', pieceSkinBlack)}
              {renderPreviewPiece('rook', 'black', pieceSkinBlack)}
              {renderPreviewPiece('bishop', 'black', pieceSkinBlack)}
              {renderPreviewPiece('knight', 'black', pieceSkinBlack)}
              {renderPreviewPiece('pawn', 'black', pieceSkinBlack)}
            </div>
            <span className="preview-skin-name">{pieceSkins.find(s => s.id === pieceSkinBlack)?.name || 'Classic'}</span>
          </div>
        </div>
        
        {onRandomize && (
          <button 
            className="randomize-skins-btn"
            onClick={onRandomize}
            data-testid="randomize-skins-btn"
          >
            🎲 {t('settings.randomizeSkins', 'Randomize Contrast')}
          </button>
        )}
      </div>
      
      {/* White Side Piece Skin Selection */}
      <div className="piece-side-section">
        <h4>⬜ {t('settings.whiteSidePieces', 'White Side Pieces')}</h4>
        <div className="piece-skin-grid">
          {pieceSkins.map(skin => (
            <button
              key={skin.id || 'classic-white'}
              className={`piece-skin-button ${pieceSkinWhite === skin.id ? 'active' : ''} ${skin.id || 'classic'}`}
              onClick={() => setPieceSkinWhite(skin.id)}
              data-testid={`piece-skin-white-${skin.id || 'classic'}`}
            >
              {skin.isDefault ? (
                <div className="piece-skin-preview-text">♔♕♖♗♘♙</div>
              ) : (
                <div className="piece-skin-preview-images">
                  {skin.preview.map((img) => (
                    <img key={img} src={img} alt={skin.name} className="piece-skin-thumb" />
                  ))}
                </div>
              )}
              <span className="piece-skin-label">{skin.name}</span>
              {skin.badge && (
                <span className={`piece-skin-badge ${skin.badge === 'Premium' ? '' : 'emoji-badge'}`}>
                  {skin.badge === 'Premium' ? t('settings.premium') : skin.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Black Side Piece Skin Selection */}
      <div className="piece-side-section">
        <h4>⬛ {t('settings.blackSidePieces', 'Black Side Pieces')}</h4>
        <div className="piece-skin-grid">
          {pieceSkins.map(skin => (
            <button
              key={skin.id || 'classic-black'}
              className={`piece-skin-button ${pieceSkinBlack === skin.id ? 'active' : ''} ${skin.id || 'classic'}`}
              onClick={() => setPieceSkinBlack(skin.id)}
              data-testid={`piece-skin-black-${skin.id || 'classic'}`}
            >
              {skin.isDefault ? (
                <div className="piece-skin-preview-text">♚♛♜♝♞♟</div>
              ) : (
                <div className="piece-skin-preview-images">
                  {skin.preview.map((img) => (
                    <img key={img} src={img} alt={skin.name} className="piece-skin-thumb" />
                  ))}
                </div>
              )}
              <span className="piece-skin-label">{skin.name}</span>
              {skin.badge && (
                <span className={`piece-skin-badge ${skin.badge === 'Premium' ? '' : 'emoji-badge'}`}>
                  {skin.badge === 'Premium' ? t('settings.premium') : skin.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      <p className="section-hint">{t('settings.skinsApplyTo', 'Skins apply to King, Queen, Rook, Bishop, Knight, Pawn')}</p>
    </section>
  );
};

export default PieceSkinSelector;
